import {
  array,
  boolean,
  exactOptional,
  type GenericSchema,
  lazy,
  literal,
  number,
  object,
  string,
  union,
  variant,
  never,
  type InferOutput,
} from "valibot";

export const Op = {
  EQUAL: "EQUAL",
  INCLUDES: "INCLUDES",
  NOT_INCLUDES: "NOT_INCLUDES",
  INCLUDES_ALL: "INCLUDES_ALL",
} as const;

export type Op = (typeof Op)[keyof typeof Op];

export const Status = {
  DRAFT: "DRAFT",
  GLOBAL: "GLOBAL",
} as const;

export type Status = (typeof Status)[keyof typeof Status];

export const QuestionType = {
  TEXT: "TEXT",
  MULTIPLE_CHOICE: "MULTIPLE_CHOICE",
  NUMBER: "NUMBER",
  DATE: "DATE",
} as const;

export type QuestionType = (typeof QuestionType)[keyof typeof QuestionType];

export const CollectionType = {
  STANDALONE: "STANDALONE",
  TWO_STAGE: "TWO_STAGE",
} as const;

export type CollectionType =
  (typeof CollectionType)[keyof typeof CollectionType];

const GuidanceSchema = object({
  text: exactOptional(string()),
  resources: exactOptional(
    array(
      object({
        text: string(),
        link: string(),
      }),
    ),
  ),
});

const BaseCondition = object({
  questionId: exactOptional(string()),
});

const EqualCondition = object({
  ...BaseCondition.entries,
  op: literal(Op.EQUAL),
  value: union([string(), number()]),
});

const IncludesCondition = object({
  ...BaseCondition.entries,
  op: union([
    literal(Op.INCLUDES),
    literal(Op.NOT_INCLUDES),
    literal(Op.INCLUDES_ALL),
  ]),
  value: array(union([string(), number()])),
});

const Condition = variant("op", [EqualCondition, IncludesCondition]);
export type Condition = InferOutput<typeof Condition>;

const BaseLogic = object({
  condition: array(Condition),
  priority: exactOptional(number()),
});

export const TargetType = {
  QUESTION: "QUESTION",
  SECTION: "SECTION",
  OUTCOME: "OUTCOME",
} as const;

export type TargetType = (typeof TargetType)[keyof typeof TargetType];

const QuestionTarget = object({
  type: literal(TargetType.QUESTION),
  value: string(),
});

// const SectionTarget = object({
//   type: literal(TargetType.SECTION),
//   value: string(),
// });

const OutcomeTarget = object({
  type: literal(TargetType.OUTCOME),
  value: string(),
});

const SkipTarget = variant("type", [QuestionTarget]);

export const LogicAction = {
  SKIP: "SKIP",
  END: "END",
  HIDE: "HIDE",
  SET_MIN: "SET_MIN",
} as const;

export type LogicAction = (typeof LogicAction)[keyof typeof LogicAction];

const SkipLogic = object({
  ...BaseLogic.entries,
  action: literal(LogicAction.SKIP),
  target: SkipTarget,
});

const EndLogic = object({
  ...BaseLogic.entries,
  action: literal(LogicAction.END),
  target: exactOptional(OutcomeTarget),
});

const SetMinLogic = object({
  ...BaseLogic.entries,
  action: literal(LogicAction.SET_MIN),
  target: OutcomeTarget,
});

const Logic = variant("action", [SkipLogic, EndLogic, SetMinLogic]);
export type Rule = InferOutput<typeof Logic>;

const FollowUpSchema: GenericSchema<{
  condition: InferOutput<typeof Condition>[];
  questions: Question[];
}> = lazy(() =>
  object({
    condition: array(Condition),
    questions: array(QuestionSchema),
  }),
);

const Question = object({
  id: string(),
  title: string(),
  subsection: exactOptional(string()),
  logic: exactOptional(array(Logic)),
  guidance: exactOptional(union([GuidanceSchema, string()])),
  followUp: exactOptional(array(FollowUpSchema)),
});

const QuestionText = object({
  ...Question.entries,
  type: literal(QuestionType.TEXT),
});

export const AdditionalQuestionType = {
  TEXT: "TEXT",
  UPLOAD: "UPLOAD",
} as const;

export type AdditionalQuestionType =
  (typeof AdditionalQuestionType)[keyof typeof AdditionalQuestionType];

const AdditionalQuestionSchema = variant("type", [
  object({
    id: string(),
    type: literal(AdditionalQuestionType.TEXT),
    title: string(),
    // Only present on the option copy saved in an answer, never on config
    value: exactOptional(string()),
  }),
  object({
    id: string(),
    type: literal(AdditionalQuestionType.UPLOAD),
    title: string(),
    // Only present on the option copy saved in an answer, never on config
    description: exactOptional(string()),
  }),
]);

export type AdditionalQuestion = InferOutput<typeof AdditionalQuestionSchema>;

const optionBase = {
  id: string(),
  value: string(),
  name: string(),
  freeText: exactOptional(boolean()),
  freeTextLabel: exactOptional(string()),
  excludeOthers: exactOptional(boolean()),
  additionalQuestions: exactOptional(array(AdditionalQuestionSchema)),
};

export const legacyAdditionalQuestionId = (optionId: string) =>
  `legacy-${optionId}`;

/**
 * Read-time normalisation: an option carries either the new
 * `additionalQuestions` format or the legacy `freeText`/`freeTextLabel` pair —
 * never both. Legacy pairs become a single TEXT additional question with a
 * derivable id, so old answers pair up without any stored linkage.
 */
export const getAdditionalQuestions = (option: {
  id: string;
  freeText?: boolean;
  freeTextLabel?: string;
  additionalQuestions?: AdditionalQuestion[];
}): AdditionalQuestion[] => {
  if (option.additionalQuestions) return option.additionalQuestions;
  if (option.freeText)
    return [
      {
        id: legacyAdditionalQuestionId(option.id),
        type: AdditionalQuestionType.TEXT,
        title: option.freeTextLabel ?? "",
      },
    ];
  return [];
};

const PassAllOption = object({
  ...optionBase,
  minimumRequired: exactOptional(boolean()),
  isAcceptable: exactOptional(boolean()),
});

const PointsOption = object({
  ...optionBase,
  points: number(),
  minimumRequired: exactOptional(never()),
  isAcceptable: exactOptional(never()),
});

const multipleChoiceBase = {
  type: literal(QuestionType.MULTIPLE_CHOICE),
  allowMultiple: exactOptional(boolean()),
};

const PassAllMultipleChoice = object({
  ...Question.entries,
  ...multipleChoiceBase,
  options: array(PassAllOption),
});

const PointsMultipleChoice = object({
  ...Question.entries,
  ...multipleChoiceBase,
  options: array(PointsOption),
});

const QuestionNumber = object({
  ...Question.entries,
  type: literal(QuestionType.NUMBER),
});

const QuestionDate = object({
  ...Question.entries,
  type: literal(QuestionType.DATE),
});

export const PassAllQuestionSchema = variant("type", [
  QuestionText,
  PassAllMultipleChoice,
  QuestionNumber,
  QuestionDate,
]);

export const PointsQuestionSchema = variant("type", [
  QuestionText,
  PointsMultipleChoice,
  QuestionNumber,
  QuestionDate,
]);

export const QuestionSchema = union([
  PassAllQuestionSchema,
  PointsQuestionSchema,
]);

export type PassAllQuestion = InferOutput<typeof PassAllQuestionSchema>;
export type PointsQuestion = InferOutput<typeof PointsQuestionSchema>;
export type Question = PassAllQuestion | PointsQuestion;

const DraftQuestionText = object({
  ...QuestionText.entries,
  readOnly: exactOptional(boolean()),
});

const DraftPassAllMultipleChoice = object({
  ...PassAllMultipleChoice.entries,
  readOnly: exactOptional(boolean()),
});

const DraftPointsMultipleChoice = object({
  ...PointsMultipleChoice.entries,
  readOnly: exactOptional(boolean()),
});

const DraftQuestionNumber = object({
  ...QuestionNumber.entries,
  readOnly: exactOptional(boolean()),
});

const DraftQuestionDate = object({
  ...QuestionDate.entries,
  readOnly: exactOptional(boolean()),
});

export const DraftPassAllQuestionSchema = variant("type", [
  DraftQuestionText,
  DraftPassAllMultipleChoice,
  DraftQuestionNumber,
  DraftQuestionDate,
]);

export const DraftPointsQuestionSchema = variant("type", [
  DraftQuestionText,
  DraftPointsMultipleChoice,
  DraftQuestionNumber,
  DraftQuestionDate,
]);

export const DraftQuestionSchema = union([
  DraftPassAllQuestionSchema,
  DraftPointsQuestionSchema,
]);

const FreeText = object({ freeTextValue: exactOptional(string()) });

const AnswerValue = union([
  array(object({ ...PassAllOption.entries, ...FreeText.entries })),
  array(object({ ...PointsOption.entries, ...FreeText.entries })),
]);

export const QuestionAnswerSchema = object({
  answer: AnswerValue,
  followUp: exactOptional(
    array(
      object({
        questionId: string(),
        title: string(),
        value: AnswerValue,
      }),
    ),
  ),
});
